import { FC, memo, useMemo } from 'react'
import { useFormState } from 'react-final-form'
import { FieldWrapper } from 'components/extend/FieldWrapper'
import { FieldIMask } from 'forms/FieldIMask'
import { FieldTextarea } from 'forms/FieldTextarea'
import { compose, eqChar, required, validateOnFixedValue } from 'forms/validators'
import { isCompanyLegal, isSoleLegal } from 'modules/customer/helpers'

import { SelectCompany } from '../../SelectCompany/SelectCompany'
import { COMPANY_LEGAL_CHAR_LENGTH, INN_CHAR_LENGTH, SOLE_LEGAL_CHAR_LENGTH, ZIP_CODE_CHAR_LENGTH } from './constants'
import { CustomerDadataDisableAddProps } from './types'

const CustomerDadataDisableAdd: FC<CustomerDadataDisableAddProps> = (props) => {
  const {
    name,
    isDadataCustomerDisable,
    selectedOptions,
    setSelectedOptions,
    debouncedGetCompany,
    company,
    isCompanyLoading,
    errorCompany,
    setErrorCompany,
    errorAddress,
    setErrorAddress,
    isDadataAddressDisable,
    setIsDadataAddressDisable,
    isLegal,
    isIp,
    isActiveManualEntryAdress,
    searchAddressValue,
    setSearchAddressValue,
    setSelectedAddressOption,
    getAddress,
    address,
    isAddressLoading
  } = props
  const form = useFormState()
  const values = name ? form.values[name] : form.values

  const CompanyDetailsView = useMemo(
    () => (
      <>
        <FieldWrapper className="flex flex-col !py-0" label="Название компании">
          <FieldTextarea minRows={1} name={name ? `${name}.company.name` : 'company.name'} validate={required} />
        </FieldWrapper>
        <FieldWrapper className="flex flex-col !py-0" label="ИНН">
          <FieldIMask
            mask="000000000000"
            name={name ? `${name}.company.inn` : 'company.inn'}
            validate={compose(eqChar([SOLE_LEGAL_CHAR_LENGTH, COMPANY_LEGAL_CHAR_LENGTH]), required)}
          />
        </FieldWrapper>
        <FieldWrapper className="flex flex-col !py-0" label="КПП">
          <FieldIMask
            mask="000000000"
            name={name ? `${name}.company.kpp` : 'company.kpp'}
            validate={validateOnFixedValue(
              compose(required, eqChar(INN_CHAR_LENGTH)),
              name ? `${name}.company.inn` : 'company.inn',
              COMPANY_LEGAL_CHAR_LENGTH
            )}
          />
          {!isCompanyLegal(values.company?.inn) && (
            <div className="pt-1 text-body-sm text-on-surface-variant">Необязательно для ИП</div>
          )}
        </FieldWrapper>
      </>
    ),
    [name, values.company?.inn]
  )

  const AddressDetailsView = useMemo(
    () => (
      <>
        <div className="grid w-full grid-cols-2 gap-4 max-md:grid-cols-1 max-md:pt-6">
          <FieldWrapper className="flex flex-col !py-0" label="Почтовый индекс">
            <FieldIMask
              mask="000000"
              name={name ? `${name}.company.address.zipCode` : 'company.address.zipCode'}
              validate={compose(eqChar(ZIP_CODE_CHAR_LENGTH), required)}
            />
          </FieldWrapper>
          <FieldWrapper className="flex flex-col !py-0" label="Название населенного пункта">
            <FieldTextarea
              minRows={1}
              name={name ? `${name}.company.address.localityName` : 'company.address.localityName'}
              validate={required}
            />
          </FieldWrapper>
        </div>
        <div className="grid w-full grid-cols-3 gap-4 max-md:grid-cols-1 max-md:pt-6">
          <FieldWrapper className="flex flex-col !py-0" label="Название улицы">
            <FieldTextarea
              minRows={1}
              name={name ? `${name}.company.address.street` : 'company.address.street'}
              validate={required}
            />
          </FieldWrapper>
          <FieldWrapper className="flex flex-col !py-0" label="Номер дома, строение">
            <FieldTextarea
              minRows={1}
              name={name ? `${name}.company.address.house` : 'company.address.house'}
              validate={required}
            />
          </FieldWrapper>
          <FieldWrapper className="flex flex-col !py-0" label="Квартира, офис">
            <FieldTextarea minRows={1} name={name ? `${name}.company.address.flat` : 'company.address.flat'} />
            {!values.company?.address?.flat?.length && (
              <div className="pt-1 text-body-sm text-on-surface-variant">Необязательно</div>
            )}
          </FieldWrapper>
        </div>
      </>
    ),
    [name, values.company?.address?.flat?.length]
  )

  const CompanySelectView = useMemo(
    () => (
      <div className="mt-4 text-on-surface-variant">
        <SelectCompany
          address={address}
          company={company}
          debouncedGetCompany={debouncedGetCompany}
          errorAddress={errorAddress}
          errorCompany={errorCompany}
          getAddress={getAddress}
          initialValue={selectedOptions?.value}
          isActiveManualEntryAdress={isActiveManualEntryAdress}
          isAddressLoading={isAddressLoading}
          isCompanyLoading={isCompanyLoading}
          isDadataAddressDisable={isDadataAddressDisable}
          isIp={isIp}
          isLegal={isLegal}
          searchAddressValue={searchAddressValue}
          selectedOptions={selectedOptions}
          setErrorAddress={setErrorAddress}
          setErrorCompany={setErrorCompany}
          setIsDadataAddressDisable={setIsDadataAddressDisable}
          setSearchAddressValue={setSearchAddressValue}
          setSelectedAddressOption={setSelectedAddressOption}
          setSelectedOptions={setSelectedOptions}
        />
      </div>
    ),
    [
      address,
      company,
      debouncedGetCompany,
      errorAddress,
      errorCompany,
      getAddress,
      isActiveManualEntryAdress,
      isAddressLoading,
      isCompanyLoading,
      isDadataAddressDisable,
      isIp,
      isLegal,
      searchAddressValue,
      selectedOptions,
      setErrorAddress,
      setErrorCompany,
      setIsDadataAddressDisable,
      setSearchAddressValue,
      setSelectedAddressOption,
      setSelectedOptions
    ]
  )

  return (
    <>
      {!isDadataCustomerDisable && !isDadataAddressDisable && CompanySelectView}
      {!isDadataCustomerDisable && isDadataAddressDisable && (
        <>
          {CompanySelectView}
          {isSoleLegal(selectedOptions?.inn) && AddressDetailsView}
        </>
      )}
      {isDadataCustomerDisable && (
        <>
          {CompanyDetailsView}
          {AddressDetailsView}
        </>
      )}
    </>
  )
}

export default memo(CustomerDadataDisableAdd)
